import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import {
  PhoneIncoming, 
  PhoneOutgoing, 
  PhoneMissed,
  RefreshCw,
  Filter,
  Download,
  Clock,
  Calendar,
  User,
  Users,
  UserPlus,
  X,
  Search
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import AddContactModal from "@/components/AddContactModal";

import { API_URL } from '../utils/api';
const API = API_URL;

function DirectionIcon({ direction }) {
  const d = direction?.toLowerCase();
  let icon, label;
  if (d === 'inbound') {
    icon = <PhoneIncoming className="w-4 h-4 text-emerald-500" />;
    label = "Inbound";
  } else if (d === 'outbound') {
    icon = <PhoneOutgoing className="w-4 h-4 text-primary" />;
    label = "Outbound";
  } else {
    icon = <PhoneMissed className="w-4 h-4 text-amber-500" />;
    label = "Missed";
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild><span>{icon}</span></TooltipTrigger>
      <TooltipContent side="right"><p>{label}</p></TooltipContent>
    </Tooltip>
  );
}

function StatusBadge({ status }) {
  const s = status?.toLowerCase();
  let color = "bg-zinc-500/20 text-zinc-400";
  
  if (s === 'ok' || s === 'success') color = "bg-emerald-500/20 text-emerald-500";
  else if (s === 'busy') color = "bg-amber-500/20 text-amber-500";
  else if (s === 'no answer' || s === 'error') color = "bg-red-500/20 text-red-500";
  
  return (
    <span className={`text-xs px-2 py-1 rounded ${color}`}>
      {status || 'Unknown'}
    </span>
  );
}

function formatDuration(seconds) {
  if (!seconds) return "0s";
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins === 0) return `${secs}s`;
  return `${mins}m ${secs}s`;
}

// Format date to local preference (DD/MM/YYYY for NZ)
function formatLocalDate(dateStr) {
  if (!dateStr) return "";
  try {
    // Parse YYYY-MM-DD format
    const [year, month, day] = dateStr.split('-');
    if (year && month && day) {
      return `${day}/${month}/${year}`;
    }
    return dateStr;
  } catch {
    return dateStr;
  }
}

// Format time to 12-hour format
function formatLocalTime(timeStr) {
  if (!timeStr) return "";
  try {
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  } catch {
    return timeStr;
  }
}

export default function CallHistory() {
  const { token } = useAuth();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);

  // Helper to format date as YYYY-MM-DD in local timezone
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Get date range for preset
  // Add 1 day buffer to "to" date to account for NZ timezone (+13 hours ahead of UTC)
  // This ensures records from "today" in NZ are always visible regardless of viewer's timezone
  const getDateRange = (preset) => {
    const today = new Date();
    // Add 1 day to today to ensure we capture NZ "today" records
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const todayStr = formatDate(today);
    const tomorrowStr = formatDate(tomorrow);
    
    switch (preset) {
      case 'today':
        // Include today and tomorrow (NZ time might be ahead)
        return { from: todayStr, to: tomorrowStr };
      case 'yesterday': {
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayStr = formatDate(yesterday);
        return { from: yesterdayStr, to: todayStr };
      }
      case 'last7days': {
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        return { from: formatDate(weekAgo), to: tomorrowStr };
      }
      case 'last30days': {
        const monthAgo = new Date(today);
        monthAgo.setDate(monthAgo.getDate() - 30);
        return { from: formatDate(monthAgo), to: tomorrowStr };
      }
      case 'last60days': {
        const twoMonthsAgo = new Date(today);
        twoMonthsAgo.setDate(twoMonthsAgo.getDate() - 60);
        return { from: formatDate(twoMonthsAgo), to: tomorrowStr };
      }
      case 'last90days': {
        const threeMonthsAgo = new Date(today);
        threeMonthsAgo.setDate(threeMonthsAgo.getDate() - 90);
        return { from: formatDate(threeMonthsAgo), to: tomorrowStr };
      }
      case 'thisMonth': {
        const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        return { from: formatDate(firstOfMonth), to: tomorrowStr };
      }
      case 'lastMonth': {
        const firstOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
        return { from: formatDate(firstOfLastMonth), to: formatDate(lastOfLastMonth) };
      }
      default:
        return { from: '', to: '' };
    }
  };

  // Default to last 30 days
  const defaultRange = getDateRange('last30days');
  
  const [filters, setFilters] = useState({
    direction: "all",
    status: "all",
    date_from: defaultRange.from,
    date_to: defaultRange.to,
    datePreset: "last30days"
  });

  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput);
      setPage(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Add contact dialog state
  const [addContactOpen, setAddContactOpen] = useState(false);
  const [newContactPhone, setNewContactPhone] = useState("");
  const [newContactName, setNewContactName] = useState("");
  const [newContactCompany, setNewContactCompany] = useState("");
  const [newContactEmail, setNewContactEmail] = useState("");
  const [newContactNotes, setNewContactNotes] = useState("");
  const [newContactType, setNewContactType] = useState("personal"); // personal or company
  const [savingContact, setSavingContact] = useState(false);

  useEffect(() => {
    // Auto-sync on first load, then fetch records
    const initializeCallHistory = async () => {
      await syncCDR(true); // silent sync
      fetchRecords();
    };
    initializeCallHistory();
  }, []);

  useEffect(() => {
    fetchRecords();
  }, [page, filters, searchQuery]);

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: "50",
        skip: String(page * 50)
      });
      
      if (filters.direction && filters.direction !== "all") params.append("direction", filters.direction);
      if (filters.status && filters.status !== "all") params.append("status", filters.status);
      if (filters.date_from) params.append("date_from", filters.date_from);
      if (filters.date_to) params.append("date_to", filters.date_to);
      if (searchQuery) params.append("search", searchQuery);

      const res = await axios.get(`${API}/cdr/records?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecords(res.data.records || []);
      setTotal(res.data.total || 0);
    } catch (error) {
      console.error("Failed to fetch records:", error);
      toast.error("Failed to load call history");
    } finally {
      setLoading(false);
    }
  };

  const syncCDR = async (silent = false) => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/cdr/sync`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!silent) {
        toast.success(res.data.message || 'CDR sync started...', { duration: 8000 });
      }
      // Poll sync status until complete
      const poll = setInterval(async () => {
        try {
          const status = await axios.get(`${API}/cdr/sync-status`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const state = status.data.sync_state;
          if (state?.status === 'complete') {
            clearInterval(poll);
            setSyncing(false);
            if (!silent) {
              toast.success(`Sync complete: ${state.records_synced || status.data.total_records} records`, { duration: 8000 });
            }
            fetchRecords();
          } else if (state?.status === 'error') {
            clearInterval(poll);
            setSyncing(false);
            if (!silent) toast.error(`Sync error: ${state.error}`);
          }
        } catch (e) {
          clearInterval(poll);
          setSyncing(false);
        }
      }, 5000);
    } catch (error) {
      if (!silent) {
        toast.error(error.response?.data?.detail || "Sync failed");
      }
      setSyncing(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(0);
  };

  // Handle date preset change
  const handleDatePresetChange = (preset) => {
    const range = getDateRange(preset);
    setFilters(prev => ({
      ...prev,
      datePreset: preset,
      date_from: range.from,
      date_to: range.to
    }));
    setPage(0);
  };

  // Handle manual date change (clears preset)
  const handleManualDateChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      datePreset: "custom"
    }));
    setPage(0);
  };

  // Open add contact dialog with phone number pre-filled
  const openAddContact = (phoneNumber) => {
    setNewContactPhone(phoneNumber);
    setNewContactName("");
    setNewContactCompany("");
    setNewContactEmail("");
    setNewContactNotes("");
    setNewContactType("personal"); // Default to personal
    setAddContactOpen(true);
  };

  // Save new contact
  const saveNewContact = async () => {
    if (!newContactName.trim()) {
      toast.error("Please enter a name");
      return;
    }

    setSavingContact(true);
    try {
      const contactData = {
        name: newContactName.trim(),
        phone_number: newContactPhone,
        source: newContactType // "personal" or "company"
      };
      
      // Add optional fields if provided
      if (newContactCompany.trim()) contactData.company = newContactCompany.trim();
      if (newContactEmail.trim()) contactData.email = newContactEmail.trim();
      if (newContactNotes.trim()) contactData.notes = newContactNotes.trim();
      
      await axios.post(`${API}/contacts`, contactData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const typeLabel = newContactType === "company" ? "company" : "personal";
      toast.success(`Contact "${newContactName}" added as ${typeLabel} contact`);
      setAddContactOpen(false);
      // Refresh records to show the new caller name
      fetchRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add contact");
    } finally {
      setSavingContact(false);
    }
  };

  const clearFilters = () => {
    const range = getDateRange('last30days');
    setFilters({
      direction: "all",
      status: "all",
      date_from: range.from,
      date_to: range.to,
      datePreset: "last30days"
    });
    setSearchInput("");
    setSearchQuery("");
    setPage(0);
  };

  const totalPages = Math.ceil(total / 50);

  return (
    <div className="p-6 space-y-6" data-testid="call-history-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight">Call History</h1>
          <p className="text-muted-foreground">View and filter CDR records from Hero Cloud PBX</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            onClick={() => { syncCDR(true).then(() => fetchRecords()); }}
            disabled={syncing || loading}
            className="gap-2"
            data-testid="refresh-history-btn"
          >
            <RefreshCw className={`w-4 h-4 ${syncing || loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button 
            onClick={() => syncCDR(false)} 
            disabled={syncing}
            className="gap-2"
            data-testid="sync-cdr-btn"
          >
            <Download className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
            Sync from Hero
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-card border-border/50">
        <CardHeader className="pb-4">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Filter className="w-4 h-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by contact name or phone number..."
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                className="pl-9 bg-secondary/50"
                data-testid="search-input"
              />
              {searchInput && (
                <button
                  onClick={() => { setSearchInput(""); setSearchQuery(""); }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  data-testid="search-clear-btn"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <div>
              <Label className="text-xs text-muted-foreground">Direction</Label>
              <Select 
                value={filters.direction} 
                onValueChange={v => handleFilterChange("direction", v)}
              >
                <SelectTrigger className="bg-secondary/50" data-testid="filter-direction">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="inbound">Inbound</SelectItem>
                  <SelectItem value="outbound">Outbound</SelectItem>
                  <SelectItem value="onnet">Internal</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs text-muted-foreground">Status</Label>
              <Select 
                value={filters.status} 
                onValueChange={v => handleFilterChange("status", v)}
              >
                <SelectTrigger className="bg-secondary/50" data-testid="filter-status">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="ok">Answered</SelectItem>
                  <SelectItem value="busy">Busy</SelectItem>
                  <SelectItem value="no answer">No Answer</SelectItem>
                  <SelectItem value="error">Error</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs text-muted-foreground">Date Range</Label>
              <Select 
                value={filters.datePreset} 
                onValueChange={handleDatePresetChange}
              >
                <SelectTrigger className="bg-secondary/50" data-testid="filter-date-preset">
                  <SelectValue placeholder="Select range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="yesterday">Yesterday</SelectItem>
                  <SelectItem value="last7days">Last 7 Days</SelectItem>
                  <SelectItem value="last30days">Last 30 Days</SelectItem>
                  <SelectItem value="last60days">Last 60 Days</SelectItem>
                  <SelectItem value="last90days">Last 90 Days</SelectItem>
                  <SelectItem value="thisMonth">This Month</SelectItem>
                  <SelectItem value="lastMonth">Last Month</SelectItem>
                  <SelectItem value="custom">Custom Range</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs text-muted-foreground">From Date</Label>
              <Input 
                type="date"
                value={filters.date_from}
                onChange={e => handleManualDateChange("date_from", e.target.value)}
                className="bg-secondary/50"
                data-testid="filter-date-from"
              />
            </div>

            <div>
              <Label className="text-xs text-muted-foreground">To Date</Label>
              <Input 
                type="date"
                value={filters.date_to}
                onChange={e => handleManualDateChange("date_to", e.target.value)}
                className="bg-secondary/50"
                data-testid="filter-date-to"
              />
            </div>

            <div className="flex items-end">
              <Button 
                variant="outline" 
                onClick={clearFilters}
                className="w-full"
                data-testid="clear-filters-btn"
              >
                Clear
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="bg-card border-border/50">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
          <TooltipProvider delayDuration={200}>
            <Table>
              <TableHeader>
                <TableRow className="border-border/50 hover:bg-transparent">
                  <TableHead className="text-muted-foreground">Direction</TableHead>
                  <TableHead className="text-muted-foreground">Date/Time</TableHead>
                  <TableHead className="text-muted-foreground">Caller From</TableHead>
                  <TableHead className="text-muted-foreground">From (Number)</TableHead>
                  <TableHead className="text-muted-foreground">Caller To</TableHead>
                  <TableHead className="text-muted-foreground">To (Number)</TableHead>
                  <TableHead className="text-muted-foreground">Answered By</TableHead>
                  <TableHead className="text-muted-foreground">Duration</TableHead>
                  <TableHead className="text-muted-foreground">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-12">
                      <RefreshCw className="w-6 h-6 animate-spin mx-auto text-muted-foreground" />
                    </TableCell>
                  </TableRow>
                ) : records.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-12 text-muted-foreground">
                      No records found. Click &quot;Sync from Hero&quot; to fetch CDR data.
                    </TableCell>
                  </TableRow>
                ) : (
                  records.map((record, idx) => (
                      <TableRow 
                        key={record.id || idx} 
                        className="border-border/30 hover:bg-secondary/30"
                        data-testid={`cdr-row-${idx}`}
                      >
                        <TableCell>
                          <DirectionIcon direction={record.direction} />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Calendar className="w-3 h-3 text-muted-foreground" />
                            <span className="font-mono text-sm">{formatLocalDate(record.date)}</span>
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <Clock className="w-3 h-3 text-muted-foreground" />
                            <span className="font-mono text-xs text-muted-foreground">{formatLocalTime(record.time)}</span>
                          </div>
                        </TableCell>
                        {/* Caller From (Name) */}
                        <TableCell>
                          {record.from_name ? (
                            <span className="text-sm">{record.from_name}</span>
                          ) : (
                            <button
                              onClick={() => openAddContact(record.a_party)}
                              className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 hover:underline"
                              data-testid={`add-from-contact-${idx}`}
                            >
                              <UserPlus className="w-3 h-3" />
                              Add
                            </button>
                          )}
                        </TableCell>
                        {/* From (Number) */}
                        <TableCell className="font-mono text-sm">{record.a_party}</TableCell>
                        {/* Caller To (Name) */}
                        <TableCell>
                          {record.to_name ? (
                            <span className="text-sm">{record.to_name}</span>
                          ) : (
                            <button
                              onClick={() => openAddContact(record.b_party)}
                              className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 hover:underline"
                              data-testid={`add-to-contact-${idx}`}
                            >
                              <UserPlus className="w-3 h-3" />
                              Add
                            </button>
                          )}
                        </TableCell>
                        {/* To (Number) */}
                        <TableCell className="font-mono text-sm">{record.b_party}</TableCell>
                        {/* Answered By - shows extension for inbound calls */}
                        <TableCell className="text-sm">
                          {record.answered_by_name ? (
                            <span>{record.answered_by_name}</span>
                          ) : record.answered_by ? (
                            <span className="font-mono">{record.answered_by}</span>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-sm">{formatDuration(record.seconds)}</TableCell>
                        <TableCell><StatusBadge status={record.status} /></TableCell>
                      </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TooltipProvider>
          </div>

          {/* Pagination */}
          {total > 50 && (
            <div className="flex items-center justify-between p-4 border-t border-border/30">
              <p className="text-sm text-muted-foreground">
                Showing {page * 50 + 1}-{Math.min((page + 1) * 50, total)} of {total} records
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  data-testid="prev-page-btn"
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => p + 1)}
                  disabled={page >= totalPages - 1}
                  data-testid="next-page-btn"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Contact Modal (shared component) */}
      <AddContactModal
        open={addContactOpen}
        onOpenChange={setAddContactOpen}
        prefillPhone={newContactPhone}
        onSaved={() => fetchCallHistory()}
      />
    </div>
  );
}
